import type { SignalType } from '@parcel/types';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { apiFetch } from './api.js';

const PAGE_SIZE = 200;

// -- Wire types --------------------------------------------------------------

export type ApiSignalType = SignalType;

export interface ApiSignalSource {
  extracted_signal_id: string;
  source_id: string | null;
  source_slug: string | null;
  source_display_name: string | null;
  source_base_url: string | null;
  source_logo_domain: string | null;
  source_filing_id: string | null;
  source_url: string | null;
  source_document_id: string | null;
  document_type: string | null;
  document_date: string | null;
}

export interface ApiSignalSummary {
  id: string;
  project_id: string;
  project_name: string | null;
  project_primary_address: string;
  project_city: string;
  project_state: string;
  signal_type: ApiSignalType;
  /** Document-extracted factual summary (LLM event signals); null for structured-source signals. */
  summary: string | null;
  filed_at: string;
  discovered_at: string;
  source_id: string | null;
  source_slug: string | null;
  source_display_name: string | null;
  source_base_url: string | null;
  source_logo_domain: string | null;
  source_filing_id: string | null;
  source_url: string | null;
  reported_cost_usd: number | null;
  reported_gross_floor_area_sf: number | null;
  sources?: ApiSignalSource[];
}

interface PaginationBlock {
  limit: number;
  offset: number;
  total: number | null;
}

export interface ApiProjectSignalsResponse {
  signals: ApiSignalSummary[];
  pagination: PaginationBlock;
  withheld?: { count: number; oldestFiledAt: string | null };
}

export interface ApiAccountSignalsResponse {
  signals: ApiSignalSummary[];
  pagination: PaginationBlock;
  withheld?: { count: number; oldestFiledAt: string | null };
}

export type SignalScope = 'subscribed' | 'all';

/**
 * Drawer signal-scope state that resets to 'subscribed' when the entity changes.
 * The reset runs during render (not in an effect), so when a new entity opens the
 * very next signals fetch uses 'subscribed' instead of issuing a stale 'all'
 * request for the previous entity's scope.
 */
export function useSignalScope(
  entityId: string | null,
): [SignalScope, (scope: SignalScope) => void] {
  const [scope, setScope] = React.useState<SignalScope>('subscribed');
  const [trackedEntityId, setTrackedEntityId] = React.useState(entityId);
  if (entityId !== trackedEntityId) {
    setTrackedEntityId(entityId);
    setScope('subscribed');
  }
  return [scope, setScope];
}

// -- Hooks -------------------------------------------------------------------

export function useProjectSignals(
  projectId: string | null,
  workspaceId: string | undefined,
  scope: SignalScope = 'all',
) {
  return useQuery({
    queryKey: ['project-signals', projectId, workspaceId, scope],
    enabled: Boolean(projectId) && Boolean(workspaceId),
    queryFn: async () => {
      const qs = new URLSearchParams({
        workspace_id: workspaceId ?? '',
        limit: String(PAGE_SIZE),
        scope,
      }).toString();
      return apiFetch<ApiProjectSignalsResponse>(
        `/projects/${encodeURIComponent(projectId ?? '')}/signals?${qs}`,
      );
    },
  });
}

export function useAccountSignals(
  accountId: string | null,
  workspaceId: string | undefined,
  scope: SignalScope = 'all',
) {
  return useQuery({
    queryKey: ['account-signals', accountId, workspaceId, scope],
    enabled: Boolean(accountId) && Boolean(workspaceId),
    queryFn: async () => {
      const qs = new URLSearchParams({
        workspace_id: workspaceId ?? '',
        limit: String(PAGE_SIZE),
        scope,
      }).toString();
      return apiFetch<ApiAccountSignalsResponse>(
        `/accounts/${encodeURIComponent(accountId ?? '')}/signals?${qs}`,
      );
    },
  });
}
